import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Clock, Film, Tv, Gamepad2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StatusBadge } from './Insider';
import PosterPlaceholder from './PosterPlaceholder';
import SiteHeader from './SiteHeader';
import WatchlistButton from './WatchlistButton';
import CompanyFinance from './CompanyFinance';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TYPE_LABEL = {
  RELEASE_DATE_SET: 'Release',
  STREAMING_RELEASE: 'Streaming',
  SEASON_PREMIERE: 'Premiere',
  THEATRICAL_RELEASE: 'In cinemas',
  TRAILER_RELEASED: 'Trailer',
  GAME_LAUNCHED: 'Game launch',
  PLATFORM_RELEASE: 'Platform release',
  EARLY_ACCESS_RELEASED: 'Early access',
  DEMO_RELEASED: 'Demo',
  BETA_STARTED: 'Beta',
  DLC_RELEASED: 'DLC',
  EXPANSION_RELEASED: 'Expansion',
};

// Parsed as a local date: new Date('2026-11-20') is UTC midnight, which is the
// 19th anywhere west of Greenwich.
const dayLabel = (d) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day)
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// Where a title goes. A game has no TMDb id and its own public page; a film or
// show with one has a public page too; anything else is graph-only.
const titleHref = (t) => {
  if (t.media_type === 'game') return `/game/${t.project_id}`;
  if (t.tmdb_id) return `/${t.media_type === 'tv' ? 'tv' : 'movie'}/${t.tmdb_id}`;
  return `/insider/project/${t.project_id}`;
};

function MediumIcon({ mediaType }) {
  const cls = 'w-3.5 h-3.5 text-gray-400 flex-shrink-0';
  if (mediaType === 'game') return <Gamepad2 className={cls} />;
  if (mediaType === 'tv') return <Tv className={cls} />;
  return <Film className={cls} />;
}

// CompanyDetail — a production company / studio page: its name, type and the
// titles it's credited on (from the graph), each linking to its Insider page.
export default function CompanyDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/insider/companies/${id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (resp.status === 404) { setNotFound(true); return; }
      if (!resp.ok) throw new Error(`Request failed (${resp.status})`);
      setCompany(await resp.json());
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <Link to="/insider" className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back to Radar
        </Link>
      </div>

      {isLoading ? (
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-gray-400">
          <Clock className="w-6 h-6 mx-auto mb-2 animate-pulse" /> Loading…
        </div>
      ) : notFound ? (
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">Company not found.</p>
          <Link to="/insider" className="text-sm text-blue-600 hover:underline mt-2 inline-block">Back to the radar</Link>
        </div>
      ) : company && (
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-videbits-50 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-7 h-7 text-videbits-500" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">{company.name}</h2>
                {company.type && <span className="text-xs uppercase tracking-wide text-gray-400">{company.type}</span>}
              </div>
            </div>
            <WatchlistButton entityType="studio" entityId={company.id} verb="follow" />
          </div>

          <CompanyFinance finance={company.finance} />

          {/* What the company has coming, across every medium. A studio that
              makes both — Nintendo, Netflix, Warner Bros. — reads as one slate
              here rather than as two lists on two pages. */}
          {(company.upcoming || []).length > 0 && (
            <section className="mb-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-5">Coming up</h3>
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                {company.upcoming.map((u, i) => (
                  <Link key={`${u.project_id}:${u.event_type}:${u.event_date}:${i}`}
                        to={titleHref(u)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    <span className="w-28 flex-shrink-0 text-xs text-gray-500">{dayLabel(u.event_date)}</span>
                    <MediumIcon mediaType={u.media_type} />
                    <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{u.title}</span>
                    <span className="text-[11px] text-gray-400">
                      {TYPE_LABEL[u.event_type] || u.event_type}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <h3 className="text-lg font-semibold text-gray-900 mb-5">Titles</h3>
          {(company.projects || []).length === 0 ? (
            <p className="text-sm text-gray-400">No tracked titles yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {company.projects.map((p) => (
                <Link key={p.id} to={titleHref({ ...p, project_id: p.id })} className="group">
                  <div className="w-full aspect-[2/3] rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all">
                    {p.poster_url ? (
                      <img src={p.poster_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <PosterPlaceholder />
                    )}
                  </div>
                  <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight mb-1">{p.title}</p>
                  <StatusBadge status={p.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
